import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  FILTER_AVAILABILITY,
  FILTER_DELIVERY_METHODS,
  FILTER_EXTRAS,
  FILTER_HEALTH_TIER_LEVELS,
  FILTER_LATITUDE,
  FILTER_LOCATION_NAME,
  FILTER_LONGITUDE,
  FILTER_POSTAL_CODE,
  FILTER_SIMILAR_BREEDS,
  SORT,
  UTM_MEDIUM
} from 'dpl/constants/query_params';
import { FEATURES } from 'dpl/shared/constants/features';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';

import { SimilarBreedFilterResource } from '../constants/resource_types';
import { RESULT_SORTS } from '../utils/constants';
import { similarBreedFilterResourceDefinition } from '../utils/queryDefinitions';
import { SIMILAR_BREEDS_FILTER_VARIANTS } from '../utils/similarBreeds';

const PAID_UTM_MEDIUMS = ['cpc', 'cpa', 'cta'];
const IS_REL_SEARCH_RESULTS_SORT = isFeatureEnabled(
  FEATURES.REL_SEARCH_RESULTS_SORT
);

export default function useSimilarBreedFilter() {
  const {
    breedSlug,
    citySlug,
    isMobileLayout,
    pbo: serverPbo,
    preappliedFilters: { stateSlug, cityName } = {}
  } = useSelector(({ server }: any) => server);
  const params = useSelector(({ queryParams }: any) => queryParams);

  // Without this the eligibility request fires on every mobile search whether
  // or not the test is running.
  const isFilterTestEnabled = isFeatureEnabled(
    FEATURES.REL_SIMILAR_BREEDS_FILTER
  );

  // The server never mixes a sorted feed, so while a sort is active the filter
  // is neither offered nor asked about - which also keeps a sorted buyer out
  // of the test's exposures. Only the sorts it honors count: the `location`
  // the breeders tab writes leaves the filter alone.
  const isSorted =
    IS_REL_SEARCH_RESULTS_SORT && RESULT_SORTS.includes(params[SORT]);

  // Only the filters both result tabs send. The puppies-only ones (gender,
  // price tier, age) are left out on purpose: including them would change the
  // query key on a tab switch and blink the filter out, and the gate it
  // renders behind counts breeders, which those filters do not narrow.
  const filters = useMemo(
    () =>
      removeEmptyValues({
        postal_code: params[FILTER_POSTAL_CODE],
        lat: params[FILTER_LATITUDE],
        long: params[FILTER_LONGITUDE],
        location: params[FILTER_LOCATION_NAME],
        state: stateSlug,
        city: cityName,
        city_slug: citySlug,
        availability: params[FILTER_AVAILABILITY],
        delivery_method_ids: params[FILTER_DELIVERY_METHODS],
        health_tier_levels: params[FILTER_HEALTH_TIER_LEVELS],
        extra_ids: params[FILTER_EXTRAS],
        pbo: serverPbo || PAID_UTM_MEDIUMS.includes(params[UTM_MEDIUM])
      }),
    [params, stateSlug, cityName, citySlug, serverPbo]
  );

  const query = useResourceQuery<SimilarBreedFilterResource>({
    ...similarBreedFilterResourceDefinition(breedSlug, filters),
    enabled:
      isFilterTestEnabled &&
      !isSorted &&
      Boolean(isMobileLayout) &&
      Boolean(breedSlug)
  });

  const meta = query.value.meta || {};

  // Both halves move independently: a control-arm visitor is eligible and gets
  // no filter, and a treatment visitor who relocates to a thin area loses it.
  //
  // `!isSorted` as well as the `enabled` above: a disabled query still hands
  // back what it cached before the sort was picked.
  const isFilterVisible =
    !isSorted &&
    meta.variant === SIMILAR_BREEDS_FILTER_VARIANTS.VARIANT &&
    Boolean(meta.eligible);

  const isRequested = Boolean(params[FILTER_SIMILAR_BREEDS]);

  return {
    isFilterVisible,
    // The param is just a query string: it survives a reload, a shared link
    // and a location change. So a stale one on a visitor who no longer has
    // the filter means the server is not mixing either.
    isMixed: isFilterVisible && isRequested,
    // Eligibility decides whether the param mixes anything, so a fresh load
    // carrying it has no answer yet. Callers that hide chrome for the mixed
    // feed hold off rather than showing it and taking it away.
    isMixPending: query.isLoading && isRequested,
    radiusInMiles: meta.radius_in_miles,
    puppyCount: meta.puppy_count
  };
}
